import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { client, errMsg } from "@/lib/api";

const TEXT = [
  ["title", "Reunion name"], ["subtitle", "Tagline"], ["dates_text", "Dates (as shown to families)"], ["location", "Location"],
  ["address", "Address"], ["contact_name", "Contact name"], ["contact_phone", "Contact phone"], ["contact_email", "Contact email"],
  ["payment_due", "Payment due date (e.g. April 1, 2027)"], ["shirt_price", "T-shirt price ($ each)"],
];

function FeesEditor({ fees, onChange }) {
  const up = (i, k, v) => onChange(fees.map((f, j) => (j === i ? { ...f, [k]: v } : f)));
  return (
    <div className="space-y-3">
      {fees.map((f, i) => (
        <div key={f.id || i} data-testid={`fee-row-${i}`} className="grid sm:grid-cols-12 gap-2 items-center">
          <input data-testid={`fee-label-input-${i}`} className="field sm:col-span-4" placeholder="Label (e.g. Adult)" value={f.label} onChange={(e) => up(i, "label", e.target.value)} />
          <input data-testid={`fee-amount-input-${i}`} className="field sm:col-span-2" inputMode="decimal" placeholder="$" value={f.amount} onChange={(e) => up(i, "amount", e.target.value)} />
          <select data-testid={`fee-per-select-${i}`} className="field sm:col-span-2" value={f.per} onChange={(e) => up(i, "per", e.target.value)}><option value="person">per person</option><option value="family">per family</option></select>
          <input data-testid={`fee-desc-input-${i}`} className="field sm:col-span-2" placeholder="Note (e.g. ages 13+)" value={f.description || ""} onChange={(e) => up(i, "description", e.target.value)} />
          <label className="sm:col-span-1 flex items-center gap-1 text-sm font-semibold"><input type="checkbox" data-testid={`fee-tbd-${i}`} className="w-5 h-5 accent-[#2B5338]" checked={!!f.tbd} onChange={(e) => up(i, "tbd", e.target.checked)} />TBD</label>
          <button type="button" data-testid={`fee-remove-${i}`} onClick={() => onChange(fees.filter((_, j) => j !== i))} className="btn-danger sm:col-span-1 !px-3" aria-label="Remove fee"><Trash2 className="w-4 h-4" /></button>
        </div>
      ))}
      <button type="button" data-testid="fee-add-button" onClick={() => onChange([...fees, { label: "", amount: "", per: "person", description: "" }])} className="btn-outline"><Plus className="w-5 h-5" />Add fee</button>
    </div>
  );
}

function PinCard() {
  const [pin, setPin] = useState("");
  const save = async (e) => {
    e.preventDefault();
    try { await client("admin").put("/admin/checkin-pin", { pin }); setPin(""); toast.success("Check-in PIN updated"); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <form onSubmit={save} className="card-warm space-y-3">
      <h3 className="text-2xl font-bold text-amber-900">Check-in PIN</h3>
      <p className="text-stone-700">Volunteers use this PIN at <b>/checkin</b>. The starting PIN is 1234 — please change it.</p>
      <div className="flex gap-3"><input data-testid="checkin-pin-input" className="field max-w-[200px]" inputMode="numeric" minLength={4} maxLength={12} placeholder="New PIN" value={pin} onChange={(e) => setPin(e.target.value)} />
        <button data-testid="checkin-pin-save" className="btn-secondary">Save PIN</button></div>
    </form>
  );
}

export default function DetailsTab() {
  const [s, setS] = useState(null);
  const qc = useQueryClient();
  useEffect(() => { client("admin").get("/admin/settings").then((r) => setS(r.data)); }, []);
  if (!s) return <p>Loading…</p>;
  const set = (k, v) => setS({ ...s, [k]: v });
  const save = async (e) => {
    e.preventDefault();
    try {
      const fees = (s.fees || []).filter((f) => f.label.trim());
      const shirt_sizes = (s.shirt_sizes || []).map((x) => x.trim()).filter(Boolean);
      setS((await client("admin").put("/admin/settings", { ...s, fees, shirt_sizes })).data);
      qc.invalidateQueries({ queryKey: ["overview"] });
      toast.success("Reunion details saved");
    } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <form onSubmit={save} className="lg:col-span-2 space-y-6" data-testid="details-form">
        <div className="card-warm space-y-4">
          <h2 className="text-3xl font-bold text-amber-950">Reunion details</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {TEXT.map(([k, l]) => <label key={k} className="block"><span className="block font-semibold mb-1">{l}</span><input data-testid={`settings-${k}-input`} className="field" value={s[k] || ""} onChange={(e) => set(k, e.target.value)} /></label>)}
          </div>
          <label className="block"><span className="block font-semibold mb-1">About the reunion</span><textarea data-testid="settings-description-input" className="field min-h-[120px]" value={s.description || ""} onChange={(e) => set("description", e.target.value)} /></label>
          <label className="block"><span className="block font-semibold mb-1">How to pay (shown to families)</span><textarea data-testid="settings-payment-instructions-input" className="field min-h-[90px]" value={s.payment_instructions || ""} onChange={(e) => set("payment_instructions", e.target.value)} /></label>
          <label className="block"><span className="block font-semibold mb-1">T-shirt sizes (one per line)</span><textarea data-testid="settings-shirt-sizes-input" className="field min-h-[110px]" value={(s.shirt_sizes || []).join("\n")} onChange={(e) => set("shirt_sizes", e.target.value.split("\n"))} /></label>
          <label className="flex items-center gap-3 text-lg font-semibold"><input type="checkbox" data-testid="settings-registration-open" className="w-6 h-6 accent-[#2B5338]" checked={s.registration_open !== false} onChange={(e) => set("registration_open", e.target.checked)} />Registration is open</label>
        </div>
        <div className="card-warm space-y-4"><h2 className="text-3xl font-bold text-amber-950">Fees</h2>
          <p className="text-stone-700">"Per person" fees appear as a choice for each family member. "Per family" fees are added once. Tick TBD when a price isn't set yet. Activity prices are set in the Activities tab.</p>
          <FeesEditor fees={s.fees || []} onChange={(v) => set("fees", v)} /></div>
        <button data-testid="settings-save-button" className="btn-primary">Save details & fees</button>
      </form>
      <div><PinCard /></div>
    </div>
  );
}
